const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Helper function to make authenticated requests
const fetchWithAuth = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };

  const response = await fetch(`${API_URL}${endpoint}`, config);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }

  return data;
};

// API methods
const api = {
  // Auth
  login: async (email, password) => {
    return fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  signup: async (name, email, password) => {
    return fetchWithAuth('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    });
  },

  forgotPassword: async (email) => {
    return fetchWithAuth('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  resetPassword: async (token, password) => {
    return fetchWithAuth('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },

  // Threats
  getThreats: async (token) => {
    return fetchWithAuth('/threats', {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  // Devices
  getDevices: async (token) => {
    return fetchWithAuth('/devices', {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  // Violations
  getViolations: async (token) => {
    return fetchWithAuth('/violations', {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  // Reports
  getReports: async (token) => {
    return fetchWithAuth('/reports', {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  // Team Management
  getCurrentTeam: async (token) => {
    return fetchWithAuth('/teams/current', {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  getTeamMembers: async (token, teamId) => {
    return fetchWithAuth(`/teams/${teamId}/members`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  getTeamInvitations: async (token, teamId) => {
    return fetchWithAuth(`/teams/${teamId}/invitations`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  inviteTeamMember: async (token, teamId, email, role) => {
    return fetchWithAuth(`/teams/${teamId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  updateTeamMemberRole: async (token, teamId, userId, role) => {
    return fetchWithAuth(`/teams/${teamId}/members/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  removeTeamMember: async (token, teamId, userId) => {
    return fetchWithAuth(`/teams/${teamId}/members/${userId}`, {
      method: 'DELETE',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  deleteInvitation: async (token, teamId, invitationId) => {
    return fetchWithAuth(`/teams/${teamId}/invitations/${invitationId}`, {
      method: 'DELETE',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  // User Settings
  updateProfile: async (data, token) => {
    return fetchWithAuth('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  updatePassword: async (currentPassword, newPassword, token) => {
    return fetchWithAuth('/user/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  // Audit Logs
  getAuditLogs: async (token) => {
    return fetchWithAuth('/audit-logs', {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },

  // Stripe Checkout
  createCheckoutSession: async (priceId, token) => {
    return fetchWithAuth('/stripe/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ priceId }),
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  },
};

// Export API_URL as default, api as named export
export default API_URL;
export { api };